import Fraction from "fraction.js";
import { initSpnSelect2 } from "./enhancedSelects";

export default class SpnTable {
  constructor(tableSelector = "[data-spn-table]") {
    this.tableSelector = tableSelector;
    this.tables = document.querySelectorAll(this.tableSelector);

    this.tables.forEach((table) => {
      this.init(table);
    });
  }

  init(table) {
    initSpnSelect2(table);

    const autoSumRows = table.querySelectorAll("[data-row-autosum]");
    autoSumRows.forEach((row) => {
      this.subjectAutoSum(row);
      this.syncRowMaxFromSelect(row);
    });

    this.attachDeleteHandler(table);
    this.attachSelectHandler(table);

    const addButtons = table.querySelectorAll("[data-row-add] button");

    if (addButtons.length) {
      addButtons.forEach((button) => {
        button.addEventListener("click", (event) => {
          event.preventDefault();
          this.addRow(table, button);
        });
      });
    } else {
      console.log("no add buttons found for table", table);
    }
  }

  attachDeleteHandler(table) {
    if (table.dataset.spnDeleteHandlerAttached === "1") {
      return;
    }

    table.dataset.spnDeleteHandlerAttached = "1";

    table.addEventListener("click", (event) => {
      const button = event.target instanceof Element ? event.target.closest("[data-row-delete]") : null;
      if (!button) {
        return;
      }

      event.preventDefault();
      this.deleteRow(button);
    });
  }

  attachSelectHandler(table) {
    if (table.dataset.spnSelectHandlerAttached === "1") {
      return;
    }

    table.dataset.spnSelectHandlerAttached = "1";

    table.addEventListener("change", (event) => {
      const select = event.target instanceof HTMLSelectElement ? event.target : null;
      if (!select || !select.closest("[data-row-autosum]")) {
        return;
      }

      this.syncRowMaxFromSelect(select.closest("[data-row-autosum]"));
    });
  }

  subjectAutoSum(row) {
    const inputs = row.querySelectorAll("input");
    const valueInputs = row.querySelectorAll("input[data-row-value]");
    const sumTarget = row.querySelector("input[data-sum-target]");
    const maxTarget = row.querySelector("input[data-sum-max]");

    const updateSum = () => {
      if (!sumTarget) {
        return;
      }

      let sum = new Fraction(0);
      valueInputs.forEach((input) => {
        sum = sum.add(this.parseFraction(input.value));
      });

      const sumValue = sum.valueOf();
      sumTarget.value = sumValue === 0 ? "" : sum.toFraction(true);

      const maxRaw = maxTarget ? maxTarget.value : "";
      const hasMax = maxTarget && String(maxRaw).trim() !== "";
      const maxValue = hasMax ? this.parseFraction(maxRaw) : null;
      const exceedsMax = hasMax && maxValue && sum.compare(maxValue) === 1;

      row.classList.toggle("table-danger", exceedsMax);
    };

    valueInputs.forEach((input) => {
      input.dataset.prevValue = input.value ?? "";

      input.addEventListener("input", (event) => {
        if (!this.handleValueInput(event, input, updateSum)) {
          return;
        }

        updateSum();
      });
    });

    inputs.forEach((input) => {
      if (!input.hasAttribute("data-row-value")) {
        input.addEventListener("change", updateSum);
      }
    });

    updateSum();
  }

  addRow(table, button) {
    const rows = Array.from(table.querySelectorAll("tr[data-row-autosum]"));
    const templateRow = table.querySelector("tr[data-row-template]") ?? (rows.length ? rows[rows.length - 1] : null);
    if (!templateRow) {
      return;
    }

    const tableName = table.dataset.spnTable ?? "";
    const oldKey = rows.length ? this.getRowKey(templateRow, tableName) : this.getTemplateKey(templateRow);
    const nextKey = this.getNextRowKey(rows, tableName);
    const newRow = templateRow.cloneNode(true);

    newRow.classList.remove("table-danger");
    newRow.classList.remove("d-none");
    newRow.removeAttribute("data-row-template");
    newRow.removeAttribute("data-row-template-key");
    this.replaceRowKey(newRow, oldKey, nextKey, tableName);
    this.resetRowInputs(newRow);

    const sumResultRow = table.querySelector("tr[data-row-add]");
    if (sumResultRow && sumResultRow.parentElement) {
      sumResultRow.parentElement.insertBefore(newRow, sumResultRow);
    } else {
      const buttonRow = button.closest("tr");
      if (buttonRow && buttonRow.parentElement) {
        buttonRow.parentElement.insertBefore(newRow, buttonRow);
      } else {
        const body = table.querySelector("tbody") ?? table;
        body.appendChild(newRow);
      }
    }

    this.subjectAutoSum(newRow);
    this.syncRowMaxFromSelect(newRow);

    // New row may contain selects
    initSpnSelect2(table);
  }

  deleteRow(button) {
    const row = button.closest("tr[data-row-autosum]");
    if (!row) {
      return;
    }

    row.remove();
  }

  syncRowMaxFromSelect(row) {
    if (!(row instanceof Element)) {
      return;
    }

    const select = row.querySelector("select");
    const maxTarget = row.querySelector("input[data-sum-max]");
    if (!select || !maxTarget) {
      return;
    }

    const selectedOption = select.options[select.selectedIndex] ?? null;
    const nextMax = selectedOption?.dataset?.rpnMax ?? "";
    maxTarget.value = nextMax;
  }

  getTemplateKey(row) {
    return row.dataset.rowTemplateKey ?? "";
  }

  getRowKey(row, tableName) {
    const raw = row.dataset.rowAutosum ?? "";
    if (!raw) {
      return "";
    }

    if (tableName && raw.startsWith(`${tableName}_`)) {
      return raw.slice(tableName.length + 1);
    }

    return raw;
  }

  getNextRowKey(rows, tableName) {
    const keys = rows.map((row) => this.getRowKey(row, tableName)).filter((key) => key !== "");

    const numericKeys = keys
      .map((key) => (String(key).match(/^\d+$/) ? Number(key) : null))
      .filter((value) => value !== null);

    if (numericKeys.length) {
      return String(Math.max(...numericKeys) + 1);
    }

    if (keys.length) {
      return `${keys[keys.length - 1]}_x`;
    }

    return "0";
  }

  replaceRowKey(row, oldKey, newKey, tableName) {
    if (tableName) {
      row.dataset.rowAutosum = `${tableName}_${newKey}`;
    } else {
      row.dataset.rowAutosum = newKey;
    }

    if (!oldKey) {
      return;
    }

    const token = `_${oldKey}_`;
    const replacement = `_${newKey}_`;

    row.querySelectorAll("[id],[name]").forEach((element) => {
      if (element.id && element.id.includes(token)) {
        element.id = element.id.replaceAll(token, replacement);
      }

      if (element.name && element.name.includes(token)) {
        element.name = element.name.replaceAll(token, replacement);
      }
    });
  }

  resetRowInputs(row) {
    row.querySelectorAll("input").forEach((input) => {
      if (input.hasAttribute("data-row-value")) {
        input.value = "";
        input.dataset.prevValue = "";
        return;
      }

      if (input.hasAttribute("data-sum-target") || input.hasAttribute("data-sum-max")) {
        input.value = "";
        return;
      }

      if (input.type === "hidden") {
        input.value = "";
        return;
      }

      if (input.type === "text" || input.type === "number") {
        input.value = "";
        input.removeAttribute("data-field-id");
      }
    });
  }

  parseFraction(value) {
    if (value === null || value === undefined || value === "") {
      return new Fraction(0);
    }

    const normalized = String(value).replace(/\s/g, "").replace(",", ".");

    try {
      return new Fraction(normalized);
    } catch (error) {
      return new Fraction(0);
    }
  }

  handleValueInput(event, input, onValidChange) {
    const currentValue = event.target.value;

    if (currentValue === "") {
      input.dataset.prevValue = "";
      onValidChange();
      return false;
    }

    if (!this.isValidHours(currentValue)) {
      event.target.value = input.dataset.prevValue ?? "";
      return false;
    }

    input.dataset.prevValue = currentValue;
    return true;
  }

  isValidHours(value) {
    const wzorzec1 = /^[0-9,./]*$/;
    const wzorzec2 = /^[0-9,.]*$/;
    const wzorzec3 = /^[0-9/]*$/;
    const alnum = wzorzec1.test(value);
    const dot = wzorzec2.test(value);
    const slash = wzorzec3.test(value);

    if (!(alnum && (dot || slash))) {
      return false;
    }

    if (!slash) {
      alert("Znak niedozwolony. \nWartości takie jak: 1 1/3 należy wpisywać jako ułamek niewłaściwy: 4/3");
      return false;
    }

    return true;
  }
}
